import { PLAN_FEATURE_LABELS_FA, type EnforcedPlanFeature } from '@bookora/shared';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE LOCK FAMILY'S WORDS — one file, no digits
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Every sentence the locked screen, the read-only banner, the «پلن بالاتر»
 * tag and the refusal notification say is composed here (panel-locked design
 * §2, owner D6 and round 4). Nothing in this file names a price, a quota or a
 * trial length: those are runtime data (Plan rows, `pricing.policy`), and
 * `test/plan-copy-no-literals.test.ts` fails on any digit in Persian copy
 * under `features/plan/**`. Plan NAMES arrive from the server
 * (`requiredPlanName`, `planName`) and are interpolated, never typed.
 *
 * The one who can act on a lock is the `settings.billing` holder; everyone
 * else is told «مدیر کلینیک» (owner round 4), never «مالک», because a
 * manager with the billing key may not own the clinic.
 */

/** What each enforced feature gives — the locked screen's pitch line. */
export const FEATURE_PITCH_FA: Readonly<Record<EnforcedPlanFeature, string>> = Object.freeze({
  accounting: 'درآمد، هزینه و سود هر خدمت رو در گزارش‌هایی با تاریخ شمسی بررسی کنید.',
  lotInventory:
    'سری ساخت و تاریخ انقضای مواد رو ثبت کنید. بوکورا مواد نزدیک‌تر به انقضا رو پیشنهاد می‌ده و هزینهٔ مواد رو بر اساس مصرف ثبت‌شده محاسبه می‌کنه.',
  campaigns: 'به گروه‌های بیماران پیامک گروهی یا مناسبتی بفرستید و هزینه رو قبل از ارسال ببینید.',
  loyalty: 'با ثبت هر جلسه، امتیاز و سطح بیمار در باشگاه مشتریان محاسبه می‌شه.',
  payroll: 'حقوق کارکنان رو با کارکرد و سهم هر ماه حساب کنید، بدون اکسل.',
  revenueShare: 'درصد سهم هر پزشک از هر خدمت رو تعریف کنید تا سهم پزشکان خودکار حساب بشه.',
  aiImport:
    'عکس دفتر، فهرست یا فاکتور رو بارگذاری کنید و اطلاعات خوانده‌شده رو بعد از بازبینی ثبت کنید.',
  apiAccess: 'سایت، اپ و نرم‌افزارهای دیگه رو با کلید API و وب‌هوک به نوبت‌ها وصل کنید.',
  branches: 'چند شعبه رو در یک پنل مدیریت کنید. هر شعبه تقویم، انبار و صندوق جدا داره.',
});

/**
 * Stands in for a plan name the server did not give (a lock whose upgrade
 * has no public plan, or plan access not loaded yet): «در پلن بالاتر».
 */
export const UNKNOWN_PLAN_NAME = 'بالاتر';

/** The inert tag beside a locked tab and a sidebar entry. */
export const PLAN_LOCK_TAG = 'پلن بالاتر';

/** Appended to a sidebar entry's accessible name when one of its tabs is locked. */
export const SIDEBAR_LOCK_SUFFIX = '(بخشی در پلن بالاتر)';

/** The collapsed rail's label tooltip for a section with a locked tab. */
export function railLockLabel(sectionLabel: string): string {
  return `${sectionLabel} · ${PLAN_LOCK_TAG}`;
}

/** The action a billing holder is offered everywhere in the family. */
export const COMPARE_PLANS_LABEL = 'مقایسهٔ پلن‌ها';

/** «این بخش در پلن {plan} است» — the section and tab lock's title. */
export function lockTitle(planName: string): string {
  return `این بخش در پلن ${planName} است`;
}

/** «{feature} در پلن {plan} است» — the card lock and the read-only banner's title. */
export function featureLockTitle(feature: EnforcedPlanFeature, planName: string): string {
  return `${PLAN_FEATURE_LABELS_FA[feature]} در پلن ${planName} است`;
}

/** The entry plan's name when the plan list is not at hand (the 1405-08 catalogue's). */
export const ENTRY_PLAN_FALLBACK_NAME = 'مطب';

/**
 * Where the clinic stands today, under the pitch. `entryPlanName` is the
 * cheapest flat plan's name from the plan list when it is loaded — the plan
 * whose features RaaS carries — else {@link ENTRY_PLAN_FALLBACK_NAME}.
 */
export function currentPlanLine(
  lock: {
    currentPlanName: string | null;
    noPlan: boolean;
    trialing: boolean;
    raas: boolean;
  },
  entryPlanName: string | null = null,
): string {
  if (lock.noPlan || !lock.currentPlanName) return 'کلینیک هنوز پلنی انتخاب نکرده است.';
  const base = lock.trialing
    ? `دورهٔ آزمایشی پلن ${lock.currentPlanName}.`
    : `پلن فعلی کلینیک: ${lock.currentPlanName}.`;
  const entry = entryPlanName ?? ENTRY_PLAN_FALLBACK_NAME;
  return lock.raas ? `${base} پلن نتیجه‌محور امکانات پلن ${entry} رو داره.` : base;
}

/** Under «مقایسهٔ پلن‌ها»: what an upgrade does NOT do. */
export const HOLDER_NOTE = 'مبلغ نهایی رو قبل از پرداخت می‌بینید و اطلاعات ثبت‌شده حفظ می‌شه.';

/** Everyone without `settings.billing`: whom to tell, and no dead button. */
export const NON_HOLDER_LINE = 'برای ارتقای پلن به مدیر کلینیک خبر بدید.';

/** The read-only banner's body (owner D6: data already created stays readable). */
export const READ_ONLY_DESCRIPTION =
  'می‌تونید اطلاعات قبلی رو ببینید و خروجی بگیرید. برای ثبت اطلاعات جدید، پلن رو ارتقا بدید.';

/** The notification a 402 FEATURE_NOT_IN_PLAN raises. */
export const REFUSAL_TITLE = 'این کار در پلن فعلی نیست';

/** A figure the server withheld by PLAN, not permission: «در پلن {plan}». */
export function planHiddenLabel(planName: string): string {
  return `در پلن ${planName}`;
}

/** The explanation behind «در پلن …» on a withheld profit figure. */
export function planHiddenHint(planName: string, canBilling: boolean): string {
  return canBilling
    ? `سود و حاشیهٔ سود در پلن ${planName} نمایش داده می‌شه. برای مشاهدهٔ اون، از «تنظیمات › اشتراک» پلن رو ارتقا بدید.`
    : `سود و حاشیهٔ سود در پلن ${planName} نمایش داده می‌شه. برای دسترسی به اون، به مدیر کلینیک خبر بدید.`;
}

/** A locked report in the reports list. */
export function lockedReportHint(planName: string, canBilling: boolean): string {
  return canBilling
    ? `این گزارش در پلن ${planName} است. برای مشاهدهٔ اون، از «تنظیمات › اشتراک» پلن رو ارتقا بدید.`
    : `این گزارش در پلن ${planName} است. برای دسترسی به اون، به مدیر کلینیک خبر بدید.`;
}
